import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiTrash2, FiX, FiCheckCircle, FiClock, FiMessageSquare, FiBell } from 'react-icons/fi';
import { api } from '../../utils/api';
import './AdminContacts.css';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await api.getContacts();
      setContacts(res.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.updateContact(id, { status });
      fetchContacts();
      if (selectedContact && selectedContact._id === id) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      try {
        await api.deleteContact(id);
        fetchContacts();
        if (selectedContact && selectedContact._id === id) {
          setSelectedContact(null);
        }
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      new: { 
        color: '#22543d', 
        bg: 'linear-gradient(135deg, #c6f6d5, #9ae6b4)',
        border: '#68d391',
        icon: FiBell
      },
      read: { 
        color: '#2c5282', 
        bg: 'linear-gradient(135deg, #bee3f8, #90cdf4)',
        border: '#63b3ed',
        icon: FiMessageSquare
      },
      replied: { 
        color: '#1e3a8a', 
        bg: 'linear-gradient(135deg, #cfe2ff, #a5c9ff)',
        border: '#60a5fa',
        icon: FiCheckCircle
      },
      archived: { 
        color: '#2d3748', 
        bg: 'linear-gradient(135deg, #e2e8f0, #cbd5e0)',
        border: '#a0aec0',
        icon: FiClock
      }
    };
    return configs[status] || configs.new;
  };

  if (loading) {
    return (
      <div className="loading-admin">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="admin-contacts">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Gestion des Messages
      </motion.h2>

      <div className="contacts-layout">
        <motion.div
          className="contacts-list"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {contacts.length === 0 ? (
            <div className="no-data">
              <FiMail className="no-data-icon" />
              <p>Aucun message</p>
            </div>
          ) : (
            contacts.map((contact, index) => {
              const statusConfig = getStatusConfig(contact.status);
              const StatusIcon = statusConfig.icon;
              return (
                <motion.div
                  key={contact._id}
                  className={`contact-card ${contact.status === 'new' ? 'new' : ''} ${selectedContact && selectedContact._id === contact._id ? 'selected' : ''}`}
                  onClick={() => setSelectedContact(contact)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ x: 4, boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)" }}
                >
                  <div className="contact-card-header">
                    <div>
                      <strong>{contact.name}</strong>
                      <span className="contact-card-email">{contact.email}</span>
                    </div>
                    <span
                      className="status-badge"
                      style={{
                        background: statusConfig.bg,
                        color: statusConfig.color,
                        borderColor: statusConfig.border
                      }}
                    >
                      <StatusIcon className="status-icon-small" />
                      {contact.status === 'new' ? 'Nouveau' :
                       contact.status === 'read' ? 'Lu' :
                       contact.status === 'replied' ? 'Répondu' : 'Archivé'}
                    </span>
                  </div>
                  <p className="contact-card-subject">{contact.subject}</p>
                  <span className="contact-card-date">
                    {new Date(contact.createdAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </motion.div>
              );
            })
          )}
        </motion.div>

        <AnimatePresence>
          {selectedContact && (
            <motion.div
              className="contact-detail"
              initial={{ opacity: 0, x: 50, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 50, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <div className="contact-detail-header">
                <h3>Message de {selectedContact.name}</h3>
                <motion.button
                  onClick={() => setSelectedContact(null)}
                  className="close-btn"
                  whileHover={{ rotate: 90, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiX />
                </motion.button>
              </div>

              <div className="contact-detail-info">
                <div className="info-item">
                  <strong>
                    <FiMail className="info-icon" />
                    Email:
                  </strong>
                  <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
                </div>
                {selectedContact.phone && (
                  <div className="info-item">
                    <strong>
                      <FiPhone className="info-icon" />
                      Téléphone:
                    </strong>
                    <a href={`tel:${selectedContact.phone}`}>{selectedContact.phone}</a>
                  </div>
                )}
                <div className="info-item">
                  <strong>
                    <FiClock className="info-icon" />
                    Date:
                  </strong>
                  <span>
                    {new Date(selectedContact.createdAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="info-item">
                  <strong>
                    <FiMessageSquare className="info-icon" />
                    Statut:
                  </strong>
                  <span
                    className="status-badge"
                    style={(() => {
                      const config = getStatusConfig(selectedContact.status);
                      return {
                        background: config.bg,
                        color: config.color,
                        borderColor: config.border
                      };
                    })()}
                  >
                    {selectedContact.status === 'new' ? 'Nouveau' :
                     selectedContact.status === 'read' ? 'Lu' :
                     selectedContact.status === 'replied' ? 'Répondu' : 'Archivé'}
                  </span>
                </div>
              </div>

              <div className="contact-detail-content">
                <h4>
                  <FiMessageSquare className="content-icon" />
                  Sujet
                </h4>
                <p>{selectedContact.subject}</p>

                <h4>
                  <FiMail className="content-icon" />
                  Message
                </h4>
                <p className="message-content">{selectedContact.message}</p>
              </div>

              <div className="contact-detail-actions">
                <div className="status-actions">
                  <label>Changer le statut:</label>
                  <select
                    value={selectedContact.status}
                    onChange={(e) => updateStatus(selectedContact._id, e.target.value)}
                    className="status-select"
                  >
                    <option value="new">Nouveau</option>
                    <option value="read">Lu</option>
                    <option value="replied">Répondu</option>
                    <option value="archived">Archivé</option>
                  </select>
                </div>
                <motion.button
                  onClick={() => handleDelete(selectedContact._id)}
                  className="btn btn-danger"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiTrash2 />
                  <span>Supprimer</span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminContacts;
